import { useFormikContext } from "formik";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { setFromArrayOfObjects } from "utils/filterObject";
import { useAgrochemicalsContext } from "context/AgrochemichalsProvider";
import { actionFormValue, actionInputName } from "../../utils/inputName";
import { snakeToCamel } from "utils/camelSnakeTransformation";

const AGRO_META_KEY_ALIASES = {
  is_mass_trapping: "isMassTraping",
};

const getAgrochemicalMetaKeys = (agroMetaValue) =>
  (Array.isArray(agroMetaValue) ? agroMetaValue : [agroMetaValue])
    .filter(Boolean)
    .map((value) => AGRO_META_KEY_ALIASES[value] || snakeToCamel(value));

const groupSubstancesOnCause = (
  activeSubstances,
  enemyId,
  isBio,
  agroMetaKeys,
) => {
  if (!activeSubstances) {
    return {};
  }
  const reducedActiveSubstances = {};
  Object.entries(activeSubstances).forEach(([substanceKey, substances]) => {
    let group = "notRecommended";

    const filteredOnBio = [];
    for (const substance of substances) {
      const { causeId, agrochemicalRead } = substance;
      const { bio } = agrochemicalRead || {};

      // filter out non same bio value substances
      if (isBio !== bio) {
        continue;
      }

      // filter out substances that don't match the plan-suggested agro meta flag
      if (
        agroMetaKeys.length &&
        !agroMetaKeys.some((key) => agrochemicalRead?.[key])
      ) {
        continue;
      }

      // group by recommendation
      if (Array.isArray(causeId)) {
        if (causeId.includes(enemyId)) {
          group = "recommended";
          filteredOnBio.push({ ...substance, group });
        } else {
          filteredOnBio.push({ ...substance, group });
        }
      }
    }

    if (filteredOnBio.length) {
      reducedActiveSubstances[substanceKey] = filteredOnBio;
      reducedActiveSubstances[substanceKey] = {
        value: reducedActiveSubstances[substanceKey],
        group,
      };
    }
  });

  return { ...reducedActiveSubstances };
};

export const useFilteredDrugSubstances = (isProposed) => {
  const { values } = useFormikContext();
  const { activeSubstances } = useAgrochemicalsContext();
  const selectedBio = actionFormValue(values, "drugAction", "bio", isProposed);
  const selectedEnemyId = values?.caused?.enemyId;

  const selectedAgroMetaValue = actionFormValue(
    values,
    "drugAction",
    "agroMetaValue",
    isProposed,
  );
  const selectedAgroMetaKeys = useMemo(
    () => getAgrochemicalMetaKeys(selectedAgroMetaValue),
    [selectedAgroMetaValue],
  );

  const filteredSubstances = useMemo(
    () =>
      groupSubstancesOnCause(
        activeSubstances,
        selectedEnemyId,
        selectedBio,
        selectedAgroMetaKeys,
      ),
    [activeSubstances, selectedEnemyId, selectedBio, selectedAgroMetaKeys],
  );

  return { filteredSubstances };
};

export const useDrugFormEffects = (
  isProposed,
  filteredSubstances,
  isViewMode,
) => {
  const { values, initialValues, setFieldValue, setFieldTouched } =
    useFormikContext();
  const [selectedPesticideId, setSelectedPesticideId] = useState();
  const { activeSubstances } = useAgrochemicalsContext();

  useEffect(() => {
    if (isViewMode || !activeSubstances) return;

    const pesticideId = actionFormValue(
      values,
      "drugAction",
      "pesticideId",
      isProposed,
    );
    if (!pesticideId || !filteredSubstances) return;

    const filteredVariations = filteredSubstances[pesticideId]?.value || [];
    const clearFields = (fieldNames) => {
      fieldNames.forEach((fieldName) => {
        const inputName = actionInputName("drugAction", fieldName, isProposed);
        setFieldValue(inputName, null).then(() => {
          setFieldTouched(inputName, true);
        });
      });
    };

    if (!filteredVariations.length) {
      clearFields([
        "pesticideId",
        "activeSubstance",
        "composition",
        "compositionUnits",
        "brandName",
        "quantity",
        "quantityUnits",
      ]);
      return;
    }

    const composition = actionFormValue(
      values,
      "drugAction",
      "composition",
      isProposed,
    );
    const compositionUnits = actionFormValue(
      values,
      "drugAction",
      "compositionUnits",
      isProposed,
    );
    if (composition == null) return;

    const compositionMatches = filteredVariations.filter(
      ({ agrochemicalRead }) =>
        `${agrochemicalRead?.composition}` === `${composition}` &&
        agrochemicalRead?.compositionUnits === compositionUnits,
    );
    if (!compositionMatches.length) {
      clearFields([
        "composition",
        "compositionUnits",
        "brandName",
        "quantity",
        "quantityUnits",
      ]);
      return;
    }

    const brandName = actionFormValue(
      values,
      "drugAction",
      "brandName",
      isProposed,
    );
    if (
      brandName &&
      !compositionMatches.some(
        ({ agrochemicalRead }) => agrochemicalRead?.brandName === brandName,
      )
    ) {
      clearFields(["brandName", "quantity", "quantityUnits"]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeSubstances,
    filteredSubstances,
    isProposed,
    isViewMode,
    values?.drugAction?.pesticideId,
    values?.drugAction?.composition,
    values?.drugAction?.compositionUnits,
    values?.drugAction?.brandName,
    values?.proposedAction?.pesticideId,
    values?.proposedAction?.composition,
    values?.proposedAction?.compositionUnits,
    values?.proposedAction?.brandName,
  ]);

  useEffect(() => {
    if (
      setFieldValue &&
      values?.drugAction?.bio !== initialValues?.drugAction?.bio
    ) {
      setFieldValue("drugAction.pesticideId", null).then(() => {
        setFieldTouched("drugAction.pesticideId", true);
      });
    }
    setSelectedPesticideId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values?.drugAction?.bio, initialValues?.drugAction?.bio]);

  useEffect(() => {
    if (
      setFieldValue &&
      values?.drugAction?.pesticideId !== initialValues?.drugAction?.pesticideId
    ) {
      setFieldValue("drugAction.composition", null).then(() =>
        setFieldTouched("drugAction.composition", true),
      );
      setFieldValue("drugAction.compositionUnits", null).then(() =>
        setFieldTouched("drugAction.compositionUnits", true),
      );
      setFieldValue("drugAction.brandName", null).then(() =>
        setFieldTouched("drugAction.brandName", true),
      );
    }

    setSelectedPesticideId(
      actionFormValue(values, "drugAction", "pesticideId", isProposed),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isProposed,
    values?.drugAction?.pesticideId,
    initialValues?.drugAction?.pesticideId,
  ]);

  useEffect(() => {
    if (!setFieldTouched || !setFieldValue || !activeSubstances) {
      return;
    }
    const selectedSubstanceInstances = activeSubstances?.[selectedPesticideId];
    const selectedSubstanceName =
      selectedSubstanceInstances?.[0]?.agrochemicalRead?.name?.nameEn || "";
    setFieldValue("drugAction.activeSubstance", selectedSubstanceName).then(
      () => setFieldTouched("drugAction.activeSubstance", true),
    );
  }, [selectedPesticideId, activeSubstances, setFieldValue, setFieldTouched]);

  useEffect(() => {
    if (
      setFieldValue &&
      values?.drugAction?.brandName !== initialValues?.drugAction?.brandName
    ) {
      setFieldValue("drugAction.quantity", "").then(() => {
        setFieldTouched("drugAction.quantity", true);
      });
      setFieldValue("drugAction.quantityUnits", "").then(() => {
        setFieldTouched("drugAction.quantityUnits", true);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values?.drugAction?.brandName, initialValues?.drugAction?.brandName]);

  return { selectedPesticideId };
};

export const useSuggestedDrugQuantity = (filteredSubstances) => {
  const { t } = useTranslation();
  const { values } = useFormikContext();

  const [suggestedQuantity, setSuggestedQuantity] = useState();

  useEffect(() => {
    const pesticideId = values?.drugAction?.pesticideId;
    const brandName = values?.drugAction?.brandName;
    const composition = values?.drugAction?.composition;
    if (filteredSubstances && pesticideId && brandName && composition) {
      const isNoRecommended =
        filteredSubstances[pesticideId]?.group === "notRecommended";
      if (isNoRecommended) {
        return;
      }
      const variations = filteredSubstances[pesticideId]?.value?.filter(
        (s) =>
          s.agrochemicalRead.brandName === brandName &&
          s.agrochemicalRead.composition == composition,
      );

      const quantity = variations?.[0]?.quantity;
      const quantityUnits = variations?.[0]?.quantityUnits;
      if (quantity && quantityUnits) {
        setSuggestedQuantity(
          quantity + " " + t(`OperationUnits.${quantityUnits}`),
        );
      } else {
        setSuggestedQuantity("");
      }
    }
  }, [
    filteredSubstances,
    values?.drugAction?.pesticideId,
    values?.drugAction?.brandName,
    values?.drugAction?.composition,
    t,
  ]);

  return { suggestedQuantity };
};

export const useDrugQuantityUnitOptions = (
  filteredSubstances,
  substanceName,
) => {
  const { t } = useTranslation();
  const options = useMemo(
    () =>
      filteredSubstances?.[substanceName]?.value && t
        ? setFromArrayOfObjects(
            filteredSubstances[substanceName]?.value,
            "quantityUnits",
          )?.map(({ quantityUnits }) => ({
            label: t(`OperationUnits.${quantityUnits}`),
            value: quantityUnits,
          }))
        : [],
    [filteredSubstances, substanceName, t],
  );

  return { quantityOptions: options };
};

export const useSelectedBrandNameActiveSubstances = (isProposed) => {
  const { t } = useTranslation();
  const { values } = useFormikContext();
  const { activeSubstances } = useAgrochemicalsContext();

  const selectedChemicalActiveSubstances = useMemo(() => {
    const selectedPesticideId = actionFormValue(
      values,
      "drugAction",
      "pesticideId",
      isProposed,
    );
    const selectedBrandName = actionFormValue(
      values,
      "drugAction",
      "brandName",
      isProposed,
    );

    const selectedActiveSubstanceChemicals =
      activeSubstances?.[selectedPesticideId];
    const chemicalWithThatBrandName = selectedActiveSubstanceChemicals?.find(
      (chem) => chem.agrochemicalRead.brandName === selectedBrandName,
    );

    return chemicalWithThatBrandName?.activeSubstances;
  }, [activeSubstances, isProposed, values]);

  const chemicalActiveSubstancesDisclaimer = useMemo(() => {
    const activeSubstancesString = selectedChemicalActiveSubstances?.length
      ? selectedChemicalActiveSubstances
          ?.map((substance) => substance?.fullName)
          .join(", ")
      : "";

    if (!activeSubstancesString) {
      return "";
    }
    return t("ActionModal.ActiveSubstancesDisclaimer", {
      disclaimer: activeSubstancesString,
    });
  }, [selectedChemicalActiveSubstances, t]);

  return chemicalActiveSubstancesDisclaimer;
};
